//  UPDATE: src/components/fulfillment/ShipOrderDialog.tsx
// - remove Shippo label/rates logic entirely
// - keep ONLY manual carrier/tracking/note + Confirm Shipped

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";

type Props = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    carrier: string | null;
    tracking_number: string | null;
    tracking_url: string | null;
    note: string | null;
  }) => void;
  defaultCarrier?: string;
  existingTracking?: {
    carrier: string | null;
    tracking_number: string | null;
    tracking_url: string | null;
  };
};

function buildTrackingUrl(carrier: string, tracking: string) {
  const t = tracking.trim();
  if (!t) return "";

  const c = (carrier || "").toLowerCase();
  if (c.includes("other")) return "";

  if (c.includes("canada"))
    return `https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${encodeURIComponent(
      t
    )}`;
  if (c.includes("ups"))
    return `https://www.ups.com/track?tracknum=${encodeURIComponent(t)}`;
  if (c.includes("fedex"))
    return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(t)}`;
  if (c.includes("dhl"))
    return `https://www.dhl.com/ca-en/home/tracking.html?tracking-id=${encodeURIComponent(
      t
    )}`;

  return "";
}

export default function ShipOrderDialog({
  open,
  loading,
  onClose,
  onSubmit,
  defaultCarrier = "UPS",
  existingTracking,
}: Props) {
  const [carrier, setCarrier] = useState(defaultCarrier);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [note, setNote] = useState("");

  const suggestedUrl = useMemo(
    () => buildTrackingUrl(carrier, trackingNumber),
    [carrier, trackingNumber]
  );

  useEffect(() => {
    if (!open) return;

    const preCarrier = existingTracking?.carrier || defaultCarrier;
    const preTn = existingTracking?.tracking_number || "";
    const preUrl = existingTracking?.tracking_url || "";

    setCarrier(preCarrier);
    setTrackingNumber(preTn);
    setTrackingUrl(preUrl);
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCarrier, existingTracking?.carrier, existingTracking?.tracking_number, existingTracking?.tracking_url]);

  const handleSubmit = () => {
    const tn = trackingNumber.trim() || null;
    const url = (trackingUrl.trim() || suggestedUrl || "").trim() || null;

    onSubmit({
      carrier: carrier || null,
      tracking_number: tn,
      tracking_url: url,
      note: note.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark as Shipped</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          select
          fullWidth
          label="Carrier"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          sx={{ mt: 2 }}
        >
          {["UPS", "Canada Post", "FedEx", "DHL", "Purolator", "Other"].map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Tracking Number (optional)"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          sx={{ mt: 2 }}
        />

        <TextField
          fullWidth
          label="Tracking URL (optional)"
          value={trackingUrl}
          onChange={(e) => setTrackingUrl(e.target.value)}
          helperText={suggestedUrl ? `Suggested: ${suggestedUrl}` : " "}
          sx={{ mt: 2 }}
        />

        <TextField
          fullWidth
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={!!loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!!loading}>
          Confirm Shipped
        </Button>
      </DialogActions>
    </Dialog>
  );
}
